(ns frontend.local-storage.repair
  "The local storage keeps all its state as one transit value. When one part of the
   value cannot be written, for example a `Transit$Tag` map key that a wrong read made,
   each write of the whole value fails and nothing is saved (DEV-8230). These functions
   find such parts, drop them, and refuse to store them.

   Cost: `repair!` writes the stored value once when the page loads, and it walks the
   value only when that check fails. A write checks only its new value, so the cost of a
   write follows the size of that value and not the size of the stored state.

   Atomicity: the check of a value and the `swap!` that stores it run in one synchronous
   call, and JavaScript runs one call at a time, so no other write comes in between.
   Two browser tabs that write the same storage key are a different matter. The last tab
   wins, as it did before, and nothing here changes that."
  (:require
   [cognitect.transit :as t]
   [frontend.logging :as logging]))

(defn write-error
  "The message of the error that the transit writer gives for `x`, for example
   \"Error: Cannot write Transit$Tag\", or nil when the writer can write `x`. Each failure
   of the writer counts, because the commit of the storage fails in the same way. A new
   writer is made for each call: a writer that failed in the middle of a write gives
   different output afterwards."
  [x]
  (try
    (t/write (t/writer :json) x)
    nil
    (catch :default error
      (or (ex-message error)
          (str error)))))

(defn writable?
  "True when the transit writer can write `x`."
  [x]
  (nil? (write-error x)))

(declare prune)

(defn- prune-map
  [{:keys [x path writable-fn]}]
  (reduce (fn [acc [k v]]
            (if-not (writable-fn k)
              (update acc :dropped conj (conj path k))
              (let [result (prune {:x           v
                                   :path        (conj path k)
                                   :writable-fn writable-fn})]
                (cond-> (update acc :dropped into (:dropped result))
                  (not= ::dropped (:value result)) (update :value assoc k (:value result))))))
          {:value   (empty x)
           :dropped []}
          x))

(defn- prune-collection
  [{:keys [x path writable-fn]}]
  (let [children (if (set? x)
                   (map (fn [item]
                          [item item])
                        x)
                   (map-indexed vector x))]
    (reduce (fn [acc [position item]]
              (let [result (prune {:x           item
                                   :path        (conj path position)
                                   :writable-fn writable-fn})]
                (cond-> (update acc :dropped into (:dropped result))
                  (not= ::dropped (:value result)) (update :value conj (:value result)))))
            {:value   (if (or (vector? x) (set? x))
                        (empty x)
                        [])
             :dropped []}
            children)))

(defn- prune
  "The result is `{:value v :dropped [path ...]}`. `:value` is `::dropped` when `x`
   itself cannot be kept."
  [{:keys [x writable-fn] :as args}]
  (cond
    (writable-fn x)
    {:value   x
     :dropped []}

    (map? x)
    (prune-map args)

    (or (vector? x) (set? x) (sequential? x))
    (prune-collection args)

    :else
    {:value   ::dropped
     :dropped [(:path args)]}))

(defn prune-unwritable
  "Drop each part of `value` that cannot be written. A map entry whose key or value
   cannot be written is dropped, and so is an element of a vector or a set. The result
   is `{:value pruned :dropped [path ...]}`. A path is a vector of the keys and the
   positions that lead to the dropped part. A value that can be written is returned as
   it is."
  ([value]
   (prune-unwritable value writable?))
  ([value writable-fn]
   (let [result (prune {:x           value
                        :path        []
                        :writable-fn writable-fn})]
     {:value   (when-not (= ::dropped (:value result))
                 (:value result))
      :dropped (:dropped result)})))

(def ^:private max-logged-paths
  10)

(defn dropped-summary
  "The text for the log about the dropped parts: how many there are, and the first
   paths. A value that is badly corrupted can have hundreds of dropped parts, so the
   text shows at most 10 paths, and it says how many more there are."
  [dropped]
  (let [shown (vec (take max-logged-paths dropped))
        more (- (count dropped) (count shown))]
    (str (count dropped) " parts, the first paths: " (pr-str shown)
         (when (pos? more)
           (str " and " more " more")))))

(defn repair!
  "Make the value in the atom `storage` writable. The parts that cannot be written are
   dropped, and a summary of the dropped paths is logged. Returns the dropped paths, or
   nil when there was nothing to drop. The change of the atom makes the storage replace
   the stored string that cannot be read back."
  [storage]
  (let [stored @storage]
    (when-not (writable? stored)
      (let [{:keys [value dropped]} (prune-unwritable stored)]
        (logging/warn "Local storage: dropped the parts that cannot be written:"
                      (dropped-summary dropped))
        (reset! storage (or value {}))
        dropped))))

(defn assoc-in-writable!
  "Store `value` at `path` in the atom `storage`, unless `value` cannot be written.
   One value that cannot be written makes each later write of the storage fail, so it
   is logged and not stored. The log has the path, the message of the writer and the
   stack, which names the caller that made the value. Returns the state of the atom.

   The path and the value are in one map, `{:path ... :value ...}`, because the project
   allows two positional arguments at most. Example:

     (assoc-in-writable! storage {:path  [:account-17 :view-mode]
                                  :value :kanban})"
  [storage {:keys [path value]}]
  (if-let [error (write-error value)]
    (do
      (logging/error "Local storage: not stored, the value cannot be written:"
                     (pr-str path)
                     error
                     (.-stack (js/Error.)))
      @storage)
    (swap! storage assoc-in path value)))
